import type { PersonalSource, PersonalSourceView } from "@/api/client/personal-sources"

/**
 * What the user sees for one source. Derived only from the server's view; the
 * browser never decides eligibility (the server re-checks it on every consent).
 */
export type PersonalSourceUiState =
  /** The admin has not enabled this source for any of the user's groups. */
  | "not_eligible"
  | "not_connected"
  | "connected"
  /** Consent stands but the Microsoft grant stopped working; data stays searchable. */
  | "reconnect_required"
  /** Withdrawn: hidden at once, deleted when the grace period ends. */
  | "withdrawn"

export function personalSourceUiState(view: PersonalSourceView): PersonalSourceUiState {
  if (view.consent_state === "withdrawn") return "withdrawn"
  if (view.consent_state === "active") {
    return view.reconnect_required ? "reconnect_required" : "connected"
  }
  // none / purged: a user who lost eligibility cannot start a new consent.
  return view.eligible ? "not_connected" : "not_eligible"
}

/** Whole days from `now` until the grace period ends; 0 once it has, null if unknown. */
export function graceDaysRemaining(
  graceEndsAt: string | null | undefined,
  now: Date
): number | null {
  if (!graceEndsAt) return null
  const end = new Date(graceEndsAt)
  if (Number.isNaN(end.getTime())) return null
  const days = Math.ceil((end.getTime() - now.getTime()) / 86_400_000)
  return Math.max(days, 0)
}

export function formatGraceDate(graceEndsAt: string | null | undefined, locale: string): string {
  if (!graceEndsAt) return ""
  const end = new Date(graceEndsAt)
  if (Number.isNaN(end.getTime())) return ""
  try {
    return end.toLocaleDateString(locale || undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  } catch {
    return ""
  }
}

/** Order and translation keys of the three sources. */
export const PERSONAL_SOURCE_KEYS: Record<PersonalSource, { title: string; description: string }> =
  {
    onedrive: {
      title: "personalSources.onedrive.title",
      description: "personalSources.onedrive.description",
    },
    mail: { title: "personalSources.mail.title", description: "personalSources.mail.description" },
    chats: {
      title: "personalSources.chats.title",
      description: "personalSources.chats.description",
    },
  }

export const PERSONAL_SOURCES_QUERY_KEY = ["personal-sources"] as const

/** True when at least one source is offered, connected or waiting out a grace period. */
export function hasVisiblePersonalSources(
  views: readonly PersonalSourceView[] | undefined
): boolean {
  return (views ?? []).some((view) => personalSourceUiState(view) !== "not_eligible")
}

/** Microsoft sign-in hosts a personal-source consent may redirect to (public and sovereign clouds). */
const MICROSOFT_LOGIN_HOSTS = new Set([
  "login.microsoftonline.com",
  "login.microsoftonline.us",
  "login.partner.microsoftonline.cn",
])

/**
 * The consent URL as a URL object, or null unless it is https on a Microsoft sign-in
 * host. The browser is only ever sent to Microsoft, whatever the server returned.
 */
export function safeMicrosoftAuthUrl(raw: string): URL | null {
  try {
    const url = new URL(raw)
    if (url.protocol !== "https:" || url.username || url.password) return null
    return MICROSOFT_LOGIN_HOSTS.has(url.hostname.toLowerCase()) ? url : null
  } catch {
    return null
  }
}
